import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  DemoWorkspace,
  WorkspaceMember,
  Permission,
  MemberRole,
  GPUQuota,
} from './types';
import { DEMO_WORKSPACE_INITIAL_DATA } from './initialData';

type GPUType = 'h200' | 'a100' | 'l4';

type State = {
  workspaces: DemoWorkspace[];
};

type Action = {
  // Workspace CRUD
  addWorkspace: (workspace: DemoWorkspace) => void;
  deleteWorkspace: (workspaceId: string) => void;
  updateWorkspace: (workspaceId: string, updates: Partial<DemoWorkspace>) => void;

  // Quota management
  updateQuota: (workspaceId: string, gpuType: GPUType, quota: GPUQuota) => void;

  // Member management
  addMember: (workspaceId: string, member: WorkspaceMember) => void;
  removeMember: (workspaceId: string, memberId: string) => void;
  updateMemberPermissions: (
    workspaceId: string,
    memberId: string,
    permissions: Partial<Permission>
  ) => void;
  updateMemberRole: (workspaceId: string, memberId: string, role: MemberRole) => void;

  // Reset
  resetToInitial: () => void;
};

type DemoWorkspaceStore = State & Action;

export const useDemoWorkspaceStore = create<DemoWorkspaceStore>()(
  persist(
    (set) => ({
      workspaces: DEMO_WORKSPACE_INITIAL_DATA,

  addWorkspace: (workspace) =>
    set((state) => ({
      workspaces: [...state.workspaces, workspace],
    })),

  deleteWorkspace: (workspaceId) =>
    set((state) => ({
      workspaces: state.workspaces.filter((w) => w.id !== workspaceId),
    })),

  updateWorkspace: (workspaceId, updates) =>
    set((state) => ({
      workspaces: state.workspaces.map((w) =>
        w.id === workspaceId ? { ...w, ...updates } : w
      ),
    })),

  updateQuota: (workspaceId, gpuType, quota) =>
    set((state) => ({
      workspaces: state.workspaces.map((w) =>
        w.id === workspaceId
          ? {
              ...w,
              quota: {
                ...w.quota,
                [gpuType]: quota,
              },
            }
          : w
      ),
    })),

  addMember: (workspaceId, member) =>
    set((state) => ({
      workspaces: state.workspaces.map((w) =>
        w.id === workspaceId
          ? {
              ...w,
              members: [...w.members, member],
            }
          : w
      ),
    })),

  removeMember: (workspaceId, memberId) =>
    set((state) => ({
      workspaces: state.workspaces.map((w) =>
        w.id === workspaceId
          ? {
              ...w,
              members: w.members.filter((m) => m.id !== memberId),
            }
          : w
      ),
    })),

  updateMemberPermissions: (workspaceId, memberId, permissions) =>
    set((state) => ({
      workspaces: state.workspaces.map((w) =>
        w.id === workspaceId
          ? {
              ...w,
              members: w.members.map((m) =>
                m.id === memberId
                  ? {
                      ...m,
                      permissions: { ...m.permissions, ...permissions },
                    }
                  : m
              ),
            }
          : w
      ),
    })),

  updateMemberRole: (workspaceId, memberId, role) =>
    set((state) => ({
      workspaces: state.workspaces.map((w) =>
        w.id === workspaceId
          ? {
              ...w,
              members: w.members.map((m) =>
                m.id === memberId ? { ...m, role } : m
              ),
            }
          : w
      ),
    })),

  resetToInitial: () =>
    set({
      workspaces: DEMO_WORKSPACE_INITIAL_DATA,
    }),
    }),
    {
      name: 'demo-workspace-storage',
    }
  )
);

// Export types for convenience
export type { DemoWorkspace, WorkspaceMember, Permission, MemberRole, GPUQuota, GPUType };
